/**
 * How many names the Memory control writes on its picture (D340 phase 3). No DOM.
 *
 * A name on every node is a block of overlapping text. At rest only the hubs (layers, groups, folders as they fit) and the
 * layer captions are named, with the few biggest things; names of the rest appear on hover, on selection (the selection's
 * connections), for Find hits, and once the picture is zoomed in past a threshold, up to a budget that grows with the zoom
 * and shrinks with the number of nodes. After RUBRIC Agentic OS v2 (Jay E, RoboNuggets, MIT; see THIRD-PARTY-NOTICES.md),
 * brain/_core.js: labels and spoke links are shed when `S.cam.k` is small or `S.dense` is set.
 */

export interface LabelBudgetInput {
  picture: 'globe' | 'map' | 'timeline';
  /** How far in the picture is, 1 being the whole of it in view (the camera over the camera that fits). */
  zoom: number;
  /** The things (not hubs) on show. */
  nodes: number;
}

export interface LabelBudget {
  /** Names for the biggest things at rest. */
  biggest: number;
  /** Names for things once zoomed in; 0 until then. */
  leaves: number;
}

/** The zoom past which names of things appear, by picture. */
export const LABEL_ZOOM: Record<LabelBudgetInput['picture'], number> = { globe: 2.4, map: 2.2, timeline: 3 };
/** The most names ever written at once. */
export const MAX_LABELS = 70;
/** A picture with more nodes than this is dense: it names fewer. */
export const DENSE_NODES = 2500;

export function labelBudget(i: LabelBudgetInput): LabelBudget {
  const biggest = i.nodes <= 6 ? i.nodes : i.nodes < 40 ? 4 : 6;
  const over = i.zoom - LABEL_ZOOM[i.picture];
  if (over < 0) return { biggest, leaves: 0 };
  const grown = Math.round(12 + over * 22);
  const leaves = Math.min(MAX_LABELS, i.nodes > DENSE_NODES ? Math.round(grown / 2) : grown);
  return { biggest, leaves: Math.min(leaves, i.nodes) };
}

export interface HubLabelBudgetInput {
  picture: LabelBudgetInput['picture'];
  zoom: number;
  /** Groups (a department, a phase, a kind) and folders on show. */
  groups: number;
  dirs: number;
}

/** Hubs named at rest: the biggest few. The rest are named on hover, selection, Find and as the picture is zoomed in. */
export const HUBS_AT_REST = 4;

/**
 * How many group and folder names are written (round 4). A hub per group is the picture's skeleton; naming every one is a
 * block of overlapping text, so at rest only the biggest {@link HUBS_AT_REST} groups are named and no folders, growing
 * with the zoom once past the picture's threshold.
 */
export function hubLabelBudget(i: HubLabelBudgetInput): { groups: number; dirs: number } {
  const over = i.zoom - LABEL_ZOOM[i.picture];
  if (over < 0) return { groups: Math.min(i.groups, HUBS_AT_REST), dirs: 0 };
  return { groups: Math.min(i.groups, 10 + Math.round(over * 10)), dirs: Math.min(i.dirs, 6 + Math.round(over * 12)) };
}
